import type { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";

/**
 * Adapts react-zoom-pan-pinch to the imperative `pinchZoom` interface that the
 * existing canvas "brain" (ImageCanvasCommon / ImageCanvasCommonNew) already
 * calls. By satisfying this interface, the entire hover/click/highlight logic —
 * including the "no hover while zoomed" guard and the scale-aware coordinate
 * mapping — keeps working verbatim, with react-zoom-pan-pinch driving pan/zoom
 * instead of the old lib/pinch-zoom.
 *
 * The brain uses: getScale(), onMouseDown/Move/Up(e, cb), reset(), enable(),
 * disable(), imageCanvasScroll(...).
 */
export default class RzppPinchAdapter {
  private _getRef: (() => ReactZoomPanPinchRef | null) | undefined;

  constructor(getRef: () => ReactZoomPanPinchRef | null) {
    this._getRef = getRef;
  }

  get _ref(): ReactZoomPanPinchRef | null {
    return this._getRef ? this._getRef() : null;
  }

  getScale(): number {
    const ref = this._ref;
    const scale = ref && ref.instance && ref.instance.transformState && ref.instance.transformState.scale;
    return scale || 1;
  }

  // Panning is handled by react-zoom-pan-pinch's own listeners on the transform
  // wrapper, so these are no-ops — except onMouseUp, which must still fire the
  // optional canvas-click callback (used by the look journey's lookCanvasClick).
  onMouseDown(): void {}
  onMouseMove(): void {}
  onMouseUp(_event?: any, cb?: () => void): void {
    if (typeof cb === "function") cb();
  }

  reset(): void {
    const ref = this._ref;
    if (ref && typeof ref.resetTransform === "function") {
      ref.resetTransform(300);
    }
  }

  // Enable/disable of panning is a prop in rzpp; left as no-ops for the POC.
  enable(): void {}
  disable(): void {}

  // Old auto-scroll-to-zone behavior (mobile). Pan is now rzpp; no-op for POC.
  imageCanvasScroll(): void {}
}
